import {
  ApiGetSeasonManualResponse,
  AvailabilityDataList,
  ChannelDataList,
  DayDataList,
  DayDataTypes,
  HotelDataList,
  RoomTypeCapacityDataList,
  RoomTypeRateDataList
} from 'src/api'
import { TreeItemI } from 'src/components/customTree/CustomTree'

export enum RowType {
  HOTEL = 'HOTEL',
  ROOM_TYPE = 'ROOM_TYPE',
  RATE = 'RATE',
  CAPACITY = 'CAPACITY',
  CAPACITY_SKELETON = 'CAPACITY_SKELETON',
  CHANNEL = 'CHANNEL',
  CHANNEL_SKELETON = 'CHANNEL_SKELETON'
}

interface BaseRow {
  id: string
  type: RowType
  fatherId?: string
}

interface BaseValueRow extends BaseRow {
  editable: boolean
  inherited: boolean
  inheritedDataList?: string[]
  dayDataType: keyof DayDataTypes
}

export interface HotelRowData {
  name: string
}

export interface HotelRow extends BaseRow {
  id: string
  type: RowType.HOTEL
  data: HotelRowData
}

export interface RoomTypeRowData {
  name: string
  availabilityDataList: AvailabilityDataList[]
  availabilityDataByDate: Record<string, AvailabilityDataList>
  hotelId: string
  totalRooms: string
}

export interface RoomTypeRow extends BaseRow {
  id: string
  type: RowType.ROOM_TYPE
  data: RoomTypeRowData
}

interface BaseRowData {
  name: string
  dayDataList: DayDataList[]
  dayDataByDate: Record<string, DayDataList>
  channelId: string
  capacityId: string
  hotelId: string
  roomTypeId: string
  rateId: string
}

export interface RateRowData extends BaseRowData {
  rateData: RoomTypeRateDataList
}

export interface RateRow extends BaseValueRow {
  id: string
  type: RowType.RATE
  showCapacities: boolean
  showChannels: boolean
  channelsQty: number
  capacitiesQty: number
  capacitiesLoaded: boolean
  channelsLoaded: boolean
  data: RateRowData
}

export interface CapacityRowData extends BaseRowData {
  capacityData: RoomTypeCapacityDataList
}

export interface CapacityRow extends BaseValueRow {
  id: string
  type: RowType.CAPACITY
  showChannels: boolean
  channelsQty: number
  channelsLoaded: boolean
  fatherId: string
  data: CapacityRowData
}

export interface ChannelRowData extends BaseRowData {
  channelData: ChannelDataList
}

export interface ChannelRow extends BaseValueRow {
  id: string
  type: RowType.CHANNEL
  fatherId: string
  data: ChannelRowData
}

export interface SkeletonRow extends BaseRow {
  id: string
  type: RowType.CAPACITY_SKELETON | RowType.CHANNEL_SKELETON
  fatherId: string
}

export type RatesDataGridRow =
  | HotelRow
  | RoomTypeRow
  | RateRow
  | CapacityRow
  | ChannelRow
  | SkeletonRow

export type RatesDataGridEditedDaysList = {
  [rowId: string]: {
    [date: string]: { [key in keyof DayDataTypes]?: DayDataTypes[key] }
  }
}

export type RatesDataGridEditedDaysListOverLimit = {
  row: RateRow | ChannelRow | CapacityRow
  date: Date
}[]
export interface RatesDataGridContextState {
  loading: boolean
  dateRange: [Date, Date]
  daysList: Date[]
  rows: RatesDataGridRow[]
  apiData?: ApiGetSeasonManualResponse
  filterVisibilityId: string
  treeItems: TreeItemI[]
  filterRateIds: string[]
  editedDaysList: RatesDataGridEditedDaysList
  editedDaysListOverLimit: RatesDataGridEditedDaysListOverLimit
  massiveModModalOpen: RateRow | ChannelRow | CapacityRow | boolean
}

export type RatesDataGridContextActionTypes = {
  SET_LOADING: boolean
  SET_HOTEL_DATA_LIST: HotelDataList[]
  SET_DATE_RANGE: [Date, Date]
  SET_ROWS: RatesDataGridRow[]
  SET_INITIAL_STATE: Partial<RatesDataGridContextState>
  UPDATE_API_DATA: {
    apiData: ApiGetSeasonManualResponse
    rows: RatesDataGridRow[]
    daysList: Date[]
  }
  CHANGE_RATE_ROW_DATA_TYPE: {
    row: RateRow | CapacityRow | ChannelRow
    dataType: keyof DayDataTypes
  }
  OPEN_RATE_ROW: {
    parentRowId: string
    type: RowType.CAPACITY | RowType.CHANNEL
  }
  REPLACE_CHILD_ROWS: {
    parentRowId: string
    type: RowType.CAPACITY | RowType.CHANNEL
    rows: RatesDataGridRow[]
  }
  CLOSE_RATE_ROW: {
    parentRowId: string
    type: RowType.CAPACITY | RowType.CHANNEL
  }
  SET_TREE_ITEMS: TreeItemI[]
  SET_FILTER_RATE_IDS: string[]
  SET_FILTER_VISIBILITY_ID: string
  RESET_FILTERS: undefined
  SET_EDITED_DATE: {
    row: RateRow | ChannelRow | CapacityRow
    date: Date
    dayData: Partial<DayDataTypes>
  }
  DISCARD_ROOM_TYPE_EDITS: string
  COMMIT_EDITED_DAYS: RatesDataGridEditedDaysList
  SET_MASSIVE_MOD_MODAL: RateRow | ChannelRow | CapacityRow | boolean
}

export type RatesDataGridContextAction = {
  [key in keyof RatesDataGridContextActionTypes]: {
    type: key
    payload: RatesDataGridContextActionTypes[key]
  }
}[keyof RatesDataGridContextActionTypes]

export type LoadedCapacityRows = { [key: string]: CapacityRow[] }

export type LoadedChannelRows = { [key: string]: ChannelRow[] }

export type RowLoaderControllerMap = { [key: string]: AbortController }
